import React from 'react';

import { AlertCircle } from 'lucide-react';

import {
	type AssumptionsForm,
	CARD_MIX_FIELDS,
	CREDIT_SHARE_REQUIRED_MESSAGE,
	mixTotal,
} from '../utils/pricingAssumptions';

import {
	qBtnSecondary,
	qBtnSm,
	qInput,
	qLabel,
	qsec,
	qsecTitle,
} from './feeStructureStyles';

interface PricingAssumptionsSectionProps {
	form: AssumptionsForm;
	edited: boolean;
	onChange: (form: AssumptionsForm) => void;
	onReset: () => void;
	/** Surcharge quote on a statement that does not show its debit share (ROC-88). */
	needsCreditShare?: boolean;
}

const formatCurrency = (value: number) =>
	value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Volume, count and card mix the quote is priced on (ROC-97, v9 "Assumptions"). */
const PricingAssumptionsSection = ({
	form,
	edited,
	onChange,
	onReset,
	needsCreditShare = false,
}: PricingAssumptionsSectionProps) => {
	const volume = parseFloat(form.monthly_volume) || 0;
	const count = parseFloat(form.transaction_count) || 0;
	const total = Math.round(mixTotal(form) * 10) / 10;
	const mixOk = Math.abs(mixTotal(form) - 100) <= 0.05;

	return (
		<section className={qsec}>
			<div className="flex justify-between items-center mb-3.5">
				<div>
					<h5 className={qsecTitle}>
						Assumptions
						<span className="inline-flex items-center h-5 px-2 ml-2 align-middle rounded-full text-[11px] font-semibold tracking-[.02em] bg-primary-50 text-primary-700 border border-blue-500">
							New
						</span>
					</h5>
					<p className="text-[13px] text-gray-500 max-w-[720px]">
						From the statement. Change them to price a different month. Each
						change is recorded on the quote.
					</p>
				</div>
				{edited && (
					<button
						type="button"
						onClick={onReset}
						className={`${qBtnSm} ${qBtnSecondary}`}
					>
						Use the statement figures
					</button>
				)}
			</div>
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				<div>
					<label htmlFor="prVol" className={qLabel}>
						Monthly volume ($)
					</label>
					<input
						id="prVol"
						inputMode="decimal"
						className={qInput}
						value={form.monthly_volume}
						onChange={(e) =>
							onChange({ ...form, monthly_volume: e.target.value })
						}
					/>
				</div>
				<div>
					<label htmlFor="prTx" className={qLabel}>
						Transactions per month
					</label>
					<input
						id="prTx"
						inputMode="numeric"
						className={qInput}
						value={form.transaction_count}
						onChange={(e) =>
							onChange({ ...form, transaction_count: e.target.value })
						}
					/>
				</div>
				<div>
					<label htmlFor="prTicket" className={qLabel}>
						Average ticket
					</label>
					<input
						id="prTicket"
						readOnly
						aria-readonly="true"
						className={qInput}
						value={formatCurrency(count ? volume / count : 0)}
					/>
					<div className="text-xs text-gray-500 mt-2">
						Volume ÷ transactions
					</div>
				</div>
			</div>
			<div className={`${qLabel} mt-4`}>Card mix (% of volume)</div>
			<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 items-end mt-1.5">
				{CARD_MIX_FIELDS.map(({ key, label }) => (
					<div key={key}>
						<label
							htmlFor={`prMix-${key}`}
							className="block text-xs text-gray-500 mb-1"
						>
							{label}
						</label>
						<input
							id={`prMix-${key}`}
							inputMode="decimal"
							aria-invalid={!mixOk}
							className={`${qInput} ${mixOk ? '' : 'border-red-500'}`}
							value={form.card_mix[key]}
							onChange={(e) =>
								onChange({
									...form,
									card_mix: { ...form.card_mix, [key]: e.target.value },
								})
							}
						/>
					</div>
				))}
				<div
					aria-live="polite"
					className={`text-[13px] font-semibold pb-2.5 ${mixOk ? 'text-green-800' : 'text-red-700'}`}
				>
					Total {total}%
				</div>
			</div>
			{needsCreditShare && (
				<div className="mt-4">
					<div
						role="alert"
						className="mb-3 flex gap-3 items-start rounded-lg border border-yellow-200 bg-yellow-50 text-yellow-900 px-4 py-3 text-sm"
					>
						<AlertCircle className="w-5 h-5 flex-none mt-px text-yellow-600" />
						<div>
							{CREDIT_SHARE_REQUIRED_MESSAGE}. Debit and prepaid cards are never
							surcharged.
						</div>
					</div>
					<label htmlFor="prCreditShare" className={qLabel}>
						Credit share (% of Visa, Mastercard, Discover and Amex volume)
					</label>
					<input
						id="prCreditShare"
						inputMode="decimal"
						className={`${qInput} max-w-[200px]`}
						value={form.credit_share}
						onChange={(e) =>
							onChange({ ...form, credit_share: e.target.value })
						}
					/>
				</div>
			)}
			{!mixOk && (
				<div
					role="alert"
					className="mt-3 flex gap-3 items-start rounded-lg border border-red-100 bg-red-50 text-red-700 px-4 py-3 text-sm"
				>
					<AlertCircle className="w-5 h-5 flex-none mt-px text-red-500" />
					<div>
						The card mix must add up to 100%. It adds up to {total}% now. Fix it
						to recalculate.
					</div>
				</div>
			)}
		</section>
	);
};

export default PricingAssumptionsSection;
